import React, { useState, useEffect, useCallback } from "react";
import { Button, InputNumber, Table, message } from "antd";
import ModalCommon from "../../components/Modal";
import { TEXT_DEF } from "../../constant/text";
import { PlusOutlined } from "@ant-design/icons";
import API, { CONFIG, UPDATE_CONFIG } from "../../network/api";

const VIP_KEYS = {
  VIP_0_5: "VIP 0 - 5",
  VIP_6_11: "VIP 6 - 11",
  VIP_12_15: "VIP 12 - 15",
  VIP_16_20: "VIP 16 - 20",
};

const SetupScore = () => {
  const [isShowModalConfirm, setIsShowModalConfirm] = useState(false);
  const [isShowModalSuccess, setIsShowModalSuccess] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Giai đoạn 1: Chìa khóa (Lưu theo key mới: VIP_0_5...)
  const [phase1Data, setPhase1Data] = useState({});

  // Giai đoạn 2: Rương báu (Lưu theo key mới: VIP_0_5...)
  const [phase2Data, setPhase2Data] = useState({});

  // Cấu hình chung (Vòng cược rút tiền nằm ở root)
  const [preWithdrawRounds, setPreWithdrawRounds] = useState(0);

  // Đào kim cương
  const [diamondMiningData, setDiamondMiningData] = useState([]);

  // Điểm thưởng đặc biệt - mảng các mức tiền (cột)
  const [bonusAmounts, setBonusAmounts] = useState([]);

  // Điểm thưởng đặc biệt - data cho từng VIP (hàng)
  const [specialBonusData, setSpecialBonusData] = useState([]);

  // Giữ lại config gốc để merge khi update (tránh mất các field không hiển thị ở UI như schedule, terms...)
  const [originalConfig, setOriginalConfig] = useState({});

  const getDataConfig = useCallback(async () => {
    try {
      const res = await API.get(`${CONFIG}`);
      const data = res.data?.data || res.data;
      setOriginalConfig(data);

      // 1. Load phase1 data
      if (data.rewardPhases?.phase1?.maxScoreByVip) {
        setPhase1Data(data.rewardPhases.phase1.maxScoreByVip);
      } else {
        // Fallback init
        setPhase1Data({ VIP_0_5: 0, VIP_6_11: 0, VIP_12_15: 0, VIP_16_20: 0 });
      }

      // 2. Load phase2 data
      if (data.rewardPhases?.phase2?.maxScoreByVip) {
        setPhase2Data(data.rewardPhases.phase2.maxScoreByVip);
      } else {
        setPhase2Data({ VIP_0_5: 0, VIP_6_11: 0, VIP_12_15: 0, VIP_16_20: 0 });
      }

      // 3. Load preWithdrawRounds (nằm ở root config)
      if (data.preWithdrawRounds !== undefined) {
        setPreWithdrawRounds(data.preWithdrawRounds);
      }

      // 4. Load diamond mining data
      if (data.diamondPoints && Array.isArray(data.diamondPoints)) {
        const diamondData = data.diamondPoints.map((item, index) => ({
          key: index + 1,
          stage: item.level || index + 1,
          cumulativeDeposit: item.minDeposit || 0,
          bettingRevenue: item.dtcRequirements || 0,
          bonusPoints: item.score || 0,
        }));
        setDiamondMiningData(diamondData);
      }

      // 5. Load special bonus data (Cấu trúc mới: Array of objects {score, ratePercent})
      if (data.specialRewards) {
        const allScores = new Set();
        const vipKeys = Object.keys(VIP_KEYS); // ["VIP_0_5", "VIP_6_11", ...]

        // Quét tất cả các mức điểm (score) có trong các mảng
        vipKeys.forEach((vipKey) => {
          const rewardsArray = data.specialRewards[vipKey];
          if (Array.isArray(rewardsArray)) {
            rewardsArray.forEach((item) => {
              if (item.score) allScores.add(item.score);
            });
          }
        });

        // Tạo các cột (bonusAmounts)
        const sortedScores = Array.from(allScores).sort((a, b) => a - b);
        let idCounter = 1;
        const amountMap = {}; // map score -> id
        const amounts = [];

        sortedScores.forEach((score) => {
          amountMap[score] = idCounter;
          amounts.push({ id: idCounter, amount: score });
          idCounter++;
        });

        setBonusAmounts(amounts);

        // Tạo dữ liệu hiển thị (rows)
        const specialData = [];
        vipKeys.forEach((vipKey) => {
          const bonuses = {};
          const rewardsArray = data.specialRewards[vipKey];

          if (Array.isArray(rewardsArray)) {
            rewardsArray.forEach((item) => {
              const amountId = amountMap[item.score];
              if (amountId) {
                // API trả về 0.42 -> UI hiển thị 42
                bonuses[amountId] = item.ratePercent ? Math.round(item.ratePercent * 100) : 0;
              }
            });
          }

          specialData.push({
            key: vipKey, // VIP_0_5
            vipLevel: VIP_KEYS[vipKey], // Text hiển thị
            bonuses,
          });
        });

        setSpecialBonusData(specialData);
      }
    } catch (err) {
      console.log(err);
      messageApi.error("Không thể tải dữ liệu cấu hình");
    }
  }, [messageApi]);

  useEffect(() => {
    getDataConfig();
  }, [getDataConfig]);

  const handleUpdateConfig = async () => {
    try {
      // Validate tổng phần trăm
      const invalidVips = specialBonusData.filter((item) => {
        const total = calculateTotalPercentage(item.key);
        return total !== 100;
      });

      if (invalidVips.length > 0) {
        const totals = invalidVips
          .map((item) => {
            const total = calculateTotalPercentage(item.key);
            return `${item.vipLevel}: ${total}%`;
          })
          .join(", ");

        message.error(`Tổng tỉ lệ phần trăm phải bằng 100%. Hiện tại: ${totals}`);
        setIsShowModalConfirm(false);
        return;
      }

      setIsShowModalConfirm(false);

      // Rebuild Config Object
      const newConfig = {
        ...originalConfig, // Giữ lại các trường không sửa (schedule, terms...)
        
        preWithdrawRounds: preWithdrawRounds, // Cập nhật root field

        rewardPhases: {
          ...originalConfig.rewardPhases,
          phase1: {
            ...originalConfig.rewardPhases?.phase1,
            name: "Chìa khóa",
            maxScoreByVip: phase1Data, // { VIP_0_5: 18, ... }
          },
          phase2: {
            ...originalConfig.rewardPhases?.phase2,
            name: "Rương báu",
            maxScoreByVip: phase2Data, // { VIP_0_5: 18, ... }
          },
        },

        diamondPoints: diamondMiningData.map((item) => ({
          level: item.stage,
          minDeposit: item.cumulativeDeposit,
          dtcRequirements: item.bettingRevenue,
          score: item.bonusPoints,
        })),

        specialRewards: {},
      };

      // Format specialRewards về dạng Array [{score, ratePercent}]
      specialBonusData.forEach((vipItem) => {
        const rewardsArray = [];
        
        bonusAmounts.forEach((amountItem) => {
          const bonusValue = vipItem.bonuses?.[amountItem.id] || 0;
          if (bonusValue > 0) {
            rewardsArray.push({
                score: amountItem.amount,
                // UI là 42 -> API lưu 0.42
                ratePercent: Number((bonusValue / 100).toFixed(2)) 
            });
          }
        });

        newConfig.specialRewards[vipItem.key] = rewardsArray;
      });

      const body = {
        config: newConfig,
      };

      await API.patch(`${UPDATE_CONFIG}`, body);
      setIsShowModalSuccess(true);

      setTimeout(() => {
        getDataConfig();
      }, 1000);
    } catch (err) {
      console.log(err);
      messageApi.error(err?.response?.data?.message || "Cập nhật thất bại");
      setIsShowModalSuccess(false);
    }
  };

  const handleShowModal = () => setIsShowModalConfirm(true);

  // --- Các hàm tiện ích xử lý UI ---

  const handleAddSpecialBonusColumn = () => {
    const newId = Math.max(...bonusAmounts.map((a) => a.id), 0) + 1;
    const newAmount = { id: newId, amount: 0 };
    setBonusAmounts((prev) => [...prev, newAmount]);
    setSpecialBonusData((prev) =>
      prev.map((item) => ({
        ...item,
        bonuses: { ...item.bonuses, [newId]: 0 },
      }))
    );
  };

  const handleUpdateBonusAmount = (id, newAmount) => {
    setBonusAmounts((prev) =>
      prev.map((item) => (item.id === id ? { ...item, amount: Number(newAmount) || 0 } : item))
    );
  };

  const calculateTotalPercentage = (vipKey, data = specialBonusData) => {
    const vipData = data.find((item) => item.key === vipKey);
    if (!vipData) return 0;
    // Làm tròn để tránh lỗi float trong JS (ví dụ 0.1 + 0.2)
    const sum = Object.values(vipData.bonuses || {}).reduce((s, val) => s + (Number(val) || 0), 0);
    return Math.round(sum * 100) / 100;
  };

  const handleUpdateBonusPercentage = (vipKey, bonusId, value) => {
    setSpecialBonusData((prev) =>
      prev.map((item) =>
        item.key === vipKey
          ? {
              ...item,
              bonuses: { ...item.bonuses, [bonusId]: Number(value) || 0 },
            }
          : item
      )
    );
  };

  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  // --- Columns Definitions ---

  const diamondMiningColumns = [
    { title: "Chặng", dataIndex: "stage", key: "stage", align: "center" },
    {
      title: "Nạp cộng dồn",
      dataIndex: "cumulativeDeposit",
      key: "cumulativeDeposit",
      align: "right",
      render: (text, record) => (
        <InputNumber
          value={text}
          min={0}
          formatter={(value) => (value ? formatNumber(value) : "")}
          parser={(value) => value.replace(/\./g, "")}
          style={{ width: "100%" }}
          onChange={(value) => {
            setDiamondMiningData((prev) =>
              prev.map((item) => (item.key === record.key ? { ...item, cumulativeDeposit: Number(value) || 0 } : item))
            );
          }}
        />
      ),
    },
    {
      title: "Doanh thu cược",
      dataIndex: "bettingRevenue",
      key: "bettingRevenue",
      align: "right",
      render: (text, record) => (
        <InputNumber
          value={text}
          min={0}
          formatter={(value) => (value ? formatNumber(value) : "")}
          parser={(value) => value.replace(/\./g, "")}
          style={{ width: "100%" }}
          onChange={(value) => {
            setDiamondMiningData((prev) =>
              prev.map((item) => (item.key === record.key ? { ...item, bettingRevenue: Number(value) || 0 } : item))
            );
          }}
        />
      ),
    },
    {
      title: "Điểm tặng",
      dataIndex: "bonusPoints",
      key: "bonusPoints",
      align: "right",
      render: (text, record) => (
        <InputNumber
          value={text}
          min={0}
          style={{ width: "100%" }}
          onChange={(value) => {
            setDiamondMiningData((prev) =>
              prev.map((item) => (item.key === record.key ? { ...item, bonusPoints: Number(value) || 0 } : item))
            );
          }}
        />
      ),
    },
  ];

  const specialBonusColumns = [
    {
      title: "VIP",
      dataIndex: "vipLevel",
      key: "vipLevel",
      width: 150,
      fixed: "left",
      onHeaderCell: () => ({
        rowSpan: 2,
        style: { fontWeight: "bold", textAlign: "center" },
      }),
    },
    {
      title: "Điểm thưởng (Tỉ lệ %)",
      key: "bonusHeader",
      align: "center",
      children: bonusAmounts.map((amountItem) => ({
        title: (
          <InputNumber
            value={amountItem.amount}
            min={0}
            style={{ width: "100px" }}
            formatter={(value) => (value ? formatNumber(value) : "")}
            parser={(value) => value.replace(/\./g, "")}
            onChange={(value) => handleUpdateBonusAmount(amountItem.id, value)}
            onClick={(e) => e.stopPropagation()}
            placeholder="Điểm"
          />
        ),
        key: `bonus_${amountItem.id}`,
        dataIndex: `bonus_${amountItem.id}`,
        align: "center",
        width: 150,
        render: (text, record) => {
          const total = calculateTotalPercentage(record.key);
          const isInvalid = total > 100;
          return (
            <InputNumber
              value={record.bonuses?.[amountItem.id] || 0}
              min={0}
              max={100}
              style={{
                width: "100%",
                borderColor: isInvalid ? "#ff4d4f" : total !== 100 ? "#faad14" : undefined,
              }}
              formatter={(v) => `${v}%`}
              parser={(v) => String(v).replace("%", "")}
              onChange={(value) => handleUpdateBonusPercentage(record.key, amountItem.id, value)}
            />
          );
        },
      })),
    },
  ];

  return (
    <div className="col-12" style={{ padding: "20px" }}>
      {contextHolder}

      {/* Section 1: Giai đoạn 1 */}
      <div style={{ marginBottom: "40px" }}>
        <h2 style={{ fontSize: "20px", fontWeight: "bold", marginBottom: "10px" }}>
          Giai đoạn 1: Chìa khóa
        </h2>
        <div style={{ marginBottom: "15px", color: "#666" }}>Điểm nhận cao nhất theo VIP</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "15px", marginBottom: "20px" }}>
          {Object.keys(VIP_KEYS).map((key) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <label style={{ minWidth: "120px" }}>{VIP_KEYS[key]}:</label>
              <InputNumber
                value={phase1Data[key]}
                min={0}
                style={{ width: 120 }}
                onChange={(value) => setPhase1Data((prev) => ({ ...prev, [key]: Number(value) || 0 }))}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Section 2: Giai đoạn 2 */}
      <div style={{ marginBottom: "40px" }}>
        <h2 style={{ fontSize: "20px", fontWeight: "bold", marginBottom: "10px" }}>
          Giai đoạn 2: Rương báu
        </h2>
        <div style={{ marginBottom: "15px", color: "#666" }}>Điểm nhận cao nhất theo VIP</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "15px", marginBottom: "20px" }}>
          {Object.keys(VIP_KEYS).map((key) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <label style={{ minWidth: "120px" }}>{VIP_KEYS[key]}:</label>
              <InputNumber
                value={phase2Data[key]}
                min={0}
                style={{ width: 120 }}
                onChange={(value) => setPhase2Data((prev) => ({ ...prev, [key]: Number(value) || 0 }))}
              />
            </div>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "15px" }}>
          <label style={{ minWidth: "200px", fontWeight: "bold" }}>Vòng cược rút tiền (Pre-withdraw):</label>
          <InputNumber
            value={preWithdrawRounds}
            min={0}
            style={{ width: 120 }}
            onChange={(value) => setPreWithdrawRounds(Number(value) || 0)}
          />
        </div>
      </div>

      {/* Section 3: Đào kim cương */}
      <div style={{ marginBottom: "40px" }}>
        <h2 style={{ fontSize: "20px", fontWeight: "bold", marginBottom: "20px" }}>Đào kim cương</h2>
        <Table columns={diamondMiningColumns} dataSource={diamondMiningData} pagination={false} bordered />
      </div>

      {/* Section 4: Điểm thưởng đặc biệt */}
      <div style={{ marginBottom: "40px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h2 style={{ fontSize: "20px", fontWeight: "bold", margin: 0 }}>Điểm thưởng đặc biệt</h2>
          <Button
            type="default"
            icon={<PlusOutlined />}
            onClick={handleAddSpecialBonusColumn}
            style={{
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
              borderColor: "#d9d9d9",
            }}
          />
        </div>
        <Table columns={specialBonusColumns} dataSource={specialBonusData} pagination={false} bordered scroll={{ x: 'max-content' }} />
      </div>

      <Button
        type="primary"
        style={{ marginTop: 20 }}
        onClick={handleShowModal}
        disabled={specialBonusData.some((item) => {
          const total = calculateTotalPercentage(item.key);
          return total > 100;
        })}
      >
        Cập nhật
      </Button>

      {isShowModalConfirm && (
        <ModalCommon
          isShow={isShowModalConfirm}
          message={TEXT_DEF.text0062}
          txtBtnAcc={TEXT_DEF.text0008}
          textBtnClose={TEXT_DEF.text0023}
          actionNext={handleUpdateConfig}
          close={() => setIsShowModalConfirm(false)}
        />
      )}
      {isShowModalSuccess && (
        <ModalCommon
          isShow={isShowModalSuccess}
          message={TEXT_DEF.text0063}
          txtBtnAcc={TEXT_DEF.text0007}
          actionNext={() => {
            setIsShowModalSuccess(false);
            getDataConfig();
          }}
          close={() => {
            setIsShowModalSuccess(false);
            getDataConfig();
          }}
        />
      )}
    </div>
  );
};

export default SetupScore;